import type { Config } from '@wagmi/core';

/**
 * Holds the wagmi config created during app bootstrap.
 *
 * @remarks
 * Every `@wagmi/core` v2 action takes the config as its first argument, unlike v1 where it was
 * resolved from a module-level singleton inside the library. Keeping the config here means call
 * sites can reach it with a plain import instead of each one having to inject
 * `WagmiConfigService`, which would otherwise mean touching the constructor of every service and
 * component that talks to the chain.
 */
let wagmiConfig: Config | undefined;

/**
 * Records the config. Called once by `WagmiConfigService` during bootstrap.
 * @param config the config produced by the AppKit wagmi adapter
 * @returns {void}
 */
export function setWagmiConfig(config: Config): void {
  wagmiConfig = config;
}

/**
 * Returns the config, or undefined when bootstrap has not run yet.
 *
 * @remarks
 * For callers that can sensibly do nothing before the wallet layer is ready.
 * @returns the config, or undefined
 */
export function tryGetWagmiConfig(): Config | undefined {
  return wagmiConfig;
}

/**
 * Returns the config, throwing when bootstrap has not run yet.
 *
 * @remarks
 * For callers that cannot proceed without it, such as signing or sending a transaction, where
 * failing loudly beats a confusing downstream error.
 * @returns the config
 */
export function requireWagmiConfig(): Config {
  if (!wagmiConfig) {
    throw new Error('Wagmi config not initialised. wagmiConfiguration() must run before this call.');
  }
  return wagmiConfig;
}
